import React, { useEffect, useMemo, useState } from "react";
import { api, resolveMedia, copyText, API } from "@/lib/api";
import { toast } from "sonner";
import { Ranking, Trophy, ShareNetwork } from "@phosphor-icons/react";

export function MVPRace({ limit = 10 }) {
  const [data, setData] = useState({ players: [] });
  const [division, setDivision] = useState("all");

  useEffect(() => {
    api.get("/mvp-race").then((r) => setData(r.data || { players: [] })).catch(() => {});
  }, []);

  const divisions = useMemo(() => Array.from(new Set((data.players || []).map((p) => p.division))).filter(Boolean), [data]);
  const rows = useMemo(() => {
    let list = data.players || [];
    if (division !== "all") list = list.filter((p) => p.division === division);
    return list.slice(0, limit);
  }, [data, division, limit]);

  const share = async () => {
    if (await copyText(`${API}/share/mvp?division=${encodeURIComponent(division)}`)) toast.success("MVP Race link copied!");
    else toast.error("Could not copy link");
  };

  if (!data.players || data.players.length === 0) return null;

  return (
    <section className="relative border-b border-white/10 bg-[#0A0A0A]" data-testid="mvp-race">
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-center justify-between gap-3 mb-8 flex-wrap">
          <div className="flex items-center gap-3">
            <Ranking size={26} weight="fill" className="text-[#FF5722]" />
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF5722] font-semibold">The chase is on</p>
              <h2 className="font-display text-3xl sm:text-4xl uppercase leading-none">MVP Race</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {divisions.length > 1 && (
              <select value={division} onChange={(e) => setDivision(e.target.value)} data-testid="mvp-division-filter" className="bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm">
                <option value="all">All Divisions</option>
                {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            )}
            <button onClick={share} data-testid="share-mvp-btn" className="flex items-center gap-1.5 border border-[#FF5722]/50 text-[#FF5722] hover:bg-[#FF5722]/10 uppercase tracking-wide text-xs font-bold px-3 py-2 transition-all duration-200">
              <ShareNetwork size={15} weight="bold" /> Share
            </button>
          </div>
        </div>

        <div className="border border-white/10 divide-y divide-white/5">
          {rows.map((p, i) => (
            <div key={p.player_id} data-testid={`mvp-row-${i}`} className="flex items-center gap-4 px-4 py-3 hover:bg-white/[0.02] transition-colors">
              <span className={`font-display text-2xl w-8 text-center tabular-nums ${i === 0 ? "text-[#FFB300]" : i < 3 ? "text-[#FF5722]" : "text-white/30"}`}>{i + 1}</span>
              <div className="w-11 h-11 rounded-full bg-[#1A1A1A] overflow-hidden shrink-0 flex items-center justify-center">
                {p.photo_url ? <img src={resolveMedia(p.photo_url)} alt={p.name} className="w-full h-full object-cover" /> : <Trophy size={18} className="text-white/30" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold uppercase truncate text-sm" data-testid={`mvp-name-${i}`}>{p.name}</p>
                <p className="text-xs text-white/40 truncate">{p.team_name} · {p.division}</p>
              </div>
              <div className="hidden sm:flex items-center gap-4 text-xs text-white/50 tabular-nums">
                <span>{p.points} PTS</span><span>{p.rebounds} REB</span><span>{p.assists} AST</span>
              </div>
              <span className="font-display text-2xl text-[#FF5722] tabular-nums w-16 text-right" data-testid={`mvp-score-${i}`}>{p.score}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
